import { createApiClient, getAuthHeaders } from "@/lib/api-client";

import type {
  DeviceConnectionsResponse,
  DeviceConnectResponse,
  DeviceDataResponse,
  DeviceDisconnectResponse,
  WhoopBodyMeasurement,
  WhoopCycle,
  WhoopDataType,
  WhoopPaginatedResponse,
  WhoopProfile,
  WhoopRecovery,
  WhoopSleep,
  WhoopWorkout,
} from "./devices-types";

// Path convention: api.{env}.letsendure.dev/{service}/{resource}
// The device service is mounted under /device (singular) per
// .cursor/rules/infrastructure-pulumi.md (URL Conventions).
const deviceApi = createApiClient({
  baseUrl: import.meta.env.VITE_API_URL ?? "",
  getAuthHeaders,
});

export async function fetchConnections(): Promise<DeviceConnectionsResponse> {
  return deviceApi.get<DeviceConnectionsResponse>("/device/connections");
}

export async function connectDevice(provider: string): Promise<DeviceConnectResponse> {
  const redirectUri = `${window.location.origin}/devices`;
  return deviceApi.get<DeviceConnectResponse>(
    `/device/${provider}/connect?redirect_uri=${encodeURIComponent(redirectUri)}`
  );
}

export async function disconnectDevice(provider: string): Promise<DeviceDisconnectResponse> {
  return deviceApi.del<DeviceDisconnectResponse>(`/device/connections/${provider}`);
}

export async function fetchDeviceData<T>(
  provider: string,
  dataType: WhoopDataType
): Promise<DeviceDataResponse<T>> {
  return deviceApi.get<DeviceDataResponse<T>>(`/device/${provider}/data/${dataType}`);
}

export async function fetchWhoopProfile(): Promise<WhoopProfile> {
  const response = await fetchDeviceData<WhoopProfile>("whoop", "profile");
  return response.data;
}

export async function fetchWhoopBody(): Promise<WhoopBodyMeasurement> {
  const response = await fetchDeviceData<WhoopBodyMeasurement>("whoop", "body");
  return response.data;
}

export async function fetchWhoopRecovery(): Promise<WhoopRecovery[]> {
  const response = await fetchDeviceData<WhoopPaginatedResponse<WhoopRecovery>>(
    "whoop",
    "recovery"
  );
  return response.data.records;
}

export async function fetchWhoopSleep(): Promise<WhoopSleep[]> {
  const response = await fetchDeviceData<WhoopPaginatedResponse<WhoopSleep>>("whoop", "sleep");
  return response.data.records;
}

export async function fetchWhoopWorkouts(): Promise<WhoopWorkout[]> {
  const response = await fetchDeviceData<WhoopPaginatedResponse<WhoopWorkout>>("whoop", "workouts");
  return response.data.records;
}

export async function fetchWhoopCycles(): Promise<WhoopCycle[]> {
  const response = await fetchDeviceData<WhoopPaginatedResponse<WhoopCycle>>("whoop", "cycles");
  return response.data.records;
}
